import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  FormControl,
  InputLabel,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';
import { User, UserRole, Task } from '../../types';
import Autocomplete from '@mui/material/Autocomplete';

interface UserFormValues {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .required('El usuario es requerido'),
  password: Yup.string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .when('$isNewUser', {
      is: true,
      then: (schema) => schema.required('La contraseña es requerida'),
      otherwise: (schema) => schema.optional()
    }),
  firstName: Yup.string().required('El nombre es requerido'),
  lastName: Yup.string().required('El apellido es requerido'),
  role: Yup.string().oneOf(Object.values(UserRole)).required('El rol es requerido')
});

const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000';

const Users: React.FC = () => {
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserTasks, setSelectedUserTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openTaskDetail, setOpenTaskDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchUser, setSearchUser] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    setSelectedUser(user || null);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = async (values: UserFormValues) => {
    try {
      if (selectedUser) {
        await axiosInstance.put(`/users/${selectedUser.id}`, values);
      } else {
        await axiosInstance.post('/users', values);
      }
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await axiosInstance.delete(`/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleRowClick = async (user: User) => {
    setSelectedUser(user);
    setOpenDialog(true);
    setLoadingTasks(true);
    try {
      const response = await axiosInstance.get(`/tasks?assignedToId=${user.id}`);
      setSelectedUserTasks(response.data.filter((t: Task) => 
        showCompletedTasks ? t.status === 'COMPLETED' : t.status !== 'COMPLETED'
      ));
    } catch (error) {
      setSelectedUserTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleToggleTasks = async () => {
    setShowCompletedTasks(!showCompletedTasks);
    setLoadingTasks(true);
    try {
      const response = await axiosInstance.get(`/tasks?assignedToId=${selectedUser?.id}`);
      setSelectedUserTasks(response.data.filter((t: Task) => 
        !showCompletedTasks ? t.status === 'COMPLETED' : t.status !== 'COMPLETED'
      ));
    } catch (error) {
      setSelectedUserTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleTaskRowClick = (task: Task) => {
    setSelectedTask(task);
    setOpenTaskDetail(true);
  };

  const handleCloseTaskDetail = () => {
    setOpenTaskDetail(false);
    setSelectedTask(null);
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user =>
    user.firstName.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.email.toLowerCase().includes(searchUser.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(users)) {
    return <Alert severity="error">Error cargando usuarios</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
        width="100%"
        px={{ xs: 0, sm: 0 }}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, textAlign: { xs: 'center', sm: 'left' }, width: { xs: '100%', sm: 'auto' }, mb: { xs: 1, sm: 0 } }}
        >
          Usuarios
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
          sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' }, borderRadius: 2, px: { xs: 0, sm: 3 }, mb: { xs: 1, sm: 0 } }}
        >
          Nuevo Usuario
        </Button>
      </Box>

      <Paper sx={{
        p: { xs: 1, sm: 2 },
        mb: { xs: 3, sm: 3 },
        width: '100%',
        maxWidth: { xs: '100vw', sm: 'none' },
        boxSizing: 'border-box',
        overflowX: { xs: 'auto', sm: 'visible' },
        borderRadius: { xs: 3, sm: 2 },
        mx: { xs: 0, sm: 0 },
        mt: { xs: 1, sm: 0 }
      }}>
        <TextField
          label="Buscar usuario"
          value={searchUser}
          onChange={e => setSearchUser(e.target.value)}
          fullWidth
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }}
        />
        <TableContainer sx={{ width: '100%' }}>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Apellido</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} onClick={() => handleRowClick(user)} style={{ cursor: 'pointer' }}>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <Box>
                      <IconButton onClick={() => handleOpenDialog(user)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(user.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedUser ? `Usuario: ${selectedUser.firstName} ${selectedUser.lastName}` : 'Usuario'}
        </DialogTitle>
        <DialogContent>
          {isEditing ? (
            <Formik<UserFormValues>
              initialValues={
                selectedUser ? {
                  email: selectedUser.email,
                  firstName: selectedUser.firstName,
                  lastName: selectedUser.lastName,
                  role: selectedUser.role
                } : {
                  email: '',
                  password: '',
                  firstName: '',
                  lastName: '',
                  role: UserRole.MANAGER
                }
              }
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched }) => (
                <Form id="user-form">
                                  <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="email"
                  label="Usuario"
                  autoComplete="off"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                  {!selectedUser && (
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="password"
                      label="Contraseña"
                      type="password"
                      autoComplete="new-password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                  )}
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="firstName"
                    label="Nombre"
                    autoComplete="off"
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="lastName"
                    label="Apellido"
                    autoComplete="off"
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Rol</InputLabel>
                    <Field
                      as={Select}
                      name="role"
                      label="Rol"
                      error={touched.role && Boolean(errors.role)}
                    >
                      {Object.values(UserRole)
                        .filter(role => role !== UserRole.CLEANER)
                        .map((role) => (
                          <MenuItem key={role} value={role}>
                            {role === UserRole.MANAGER && 'Gerente'}
                            {role === UserRole.DIRECTOR && 'Director'}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Form>
              )}
            </Formik>
          ) : (
            <>
              {selectedUser && (
                <Box mb={2}>
                  <Typography><b>Usuario:</b> {selectedUser.email}</Typography>
                  <Typography><b>Rol:</b> {selectedUser.role}</Typography>
                  <Typography><b>Creado:</b> {new Date(selectedUser.createdAt).toLocaleString()}</Typography>
                  <Typography><b>Actualizado:</b> {new Date(selectedUser.updatedAt).toLocaleString()}</Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {showCompletedTasks ? 'Historial de tareas completadas' : 'Tareas pendientes asignadas'}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleToggleTasks}
                  sx={{ ml: 2 }}
                >
                  {showCompletedTasks ? 'Ver tareas pendientes' : 'Ver historial'}
                </Button>
              </Box>
              {loadingTasks ? (
                <CircularProgress />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre de Servicio</TableCell>
                      <TableCell>Fecha y Hora</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Responsable</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedUserTasks.map((task) => (
                      <TableRow key={task.id} style={{ cursor: 'pointer' }} onClick={() => handleTaskRowClick(task)}>
                        <TableCell>{task.location}</TableCell>
                        <TableCell>{new Date(task.dateTime).toLocaleString()}</TableCell>
                        <TableCell>{task.company?.name || ''}</TableCell>
                        <TableCell>{task.responsible ? `${task.responsible.firstName} ${task.responsible.lastName}` : ''}</TableCell>
                        <TableCell>{task.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cerrar</Button>
          {!isEditing ? (
            <>
              <Button 
                onClick={handleEdit}
                color="primary" 
                variant="contained"
              >
                Editar
              </Button>
              <Button 
                onClick={() => {
                  if (selectedUser) {
                    handleDelete(selectedUser.id);
                    handleCloseDialog();
                  }
                }} 
                color="error" 
                variant="contained"
              >
                Eliminar
              </Button>
            </>
          ) : (
            <Button 
              type="submit"
              form="user-form"
              color="primary" 
              variant="contained"
            >
              Guardar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={openTaskDetail} onClose={handleCloseTaskDetail} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle de la tarea</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box>
              <Typography><b>Nombre de Servicio:</b> {selectedTask.location}</Typography>
              <Typography><b>Fecha y hora:</b> {new Date(selectedTask.dateTime).toLocaleString()}</Typography>
              <Typography><b>Estado:</b> {selectedTask.status}</Typography>
              <Typography><b>Responsable:</b> {selectedTask.responsible ? `${selectedTask.responsible.firstName} ${selectedTask.responsible.lastName}` : 'No asignado'}</Typography>
              {selectedTask.notes && <Typography><b>Notas:</b> {selectedTask.notes}</Typography>}
              <Box mt={2}>
                {selectedTask.beforePhoto && selectedTask.beforePhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto antes:</Typography>
                    {selectedTask.beforePhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Antes" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
                {selectedTask.duringPhoto && selectedTask.duringPhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto durante:</Typography>
                    {selectedTask.duringPhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Durante" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
                {selectedTask.afterPhoto && selectedTask.afterPhoto.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Foto después:</Typography>
                    {selectedTask.afterPhoto.map((url, idx) => (
                      <img key={idx} src={url.startsWith('http') ? url : `${backendUrl}${url.startsWith('/') ? url : `/uploads/${url}`}`} alt="Después" style={{ maxWidth: '100%', marginBottom: 8 }} />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTaskDetail}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 